import React from 'react';
import { Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { GroupCreateAPI } from '../../../../redux/groups/api/group.model';
import { getPolyglot } from '../../../../i18n';
import { useAppDispatch, useAppSelector } from '../../../../redux/store.model';
import { useShowErrorMessage } from '../../../../handlingErrors';
import { useShowMessage } from '../../../../util/hooks';
import { getMachinesTypeSelector } from '../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { IconUpload } from '../../../../redux/groups/groups.model';
import { postGroup } from '../../../../redux/groups/actions/thunks';
import { DialogModal, ModalContent } from '../../../../theme/components';
import { ComposableDeviceForm } from './ComposableDeviceForm';

interface ComposableDeviceProps {
  closeAddComposableDevice: () => void;
  showAddComposableDevice: boolean;
  groupNameParent?: string;
  groupIdParent?: string;
}
export interface ComposableDeviceValues extends GroupCreateAPI {
  imageURL: string;
  logoURL: string;
}
export function FFComposableDeviceAdd({
  showAddComposableDevice,
  closeAddComposableDevice,
  groupIdParent,
}: ComposableDeviceProps) {
  const polyglot = getPolyglot();

  const dispatch = useAppDispatch();

  const showError = useShowErrorMessage();
  const showSuccess = useShowMessage();
  const machineTypes = useAppSelector(getMachinesTypeSelector);
  const [logo, setLogo] = useState<IconUpload | null>(null);
  const [image, setImage] = useState<IconUpload | null>(null);

  const initialValues: ComposableDeviceValues = {
    name: '',
    parentGroupID: groupIdParent ?? '',
    composableDevice: {
      machineTypeId: machineTypes[0].id,
    },
    imageURL: '',
    logoURL: '',
    devices: [],
  };

  const validationSchema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .required(polyglot.t('composable_device.name_required')),
        composableDevice: yup.object().shape({
          labels: yup.object().shape({
            businessModel: yup.string().required(),
          }),
        }),
        devices: yup
          .array()
          .of(yup.string())
          .min(1, polyglot.t('composable_device.devices_required'))
          .required(polyglot.t('composable_device.devices_required'))
          .test(
            'devices-equality',
            polyglot.t('fours_fringand.oven.devices_must_be_unique'),
            function (items) {
              const filteredItems = items.filter(
                (item) => item && item?.trim()?.length > 0
              );
              return new Set(filteredItems).size === filteredItems.length;
            }
          )
          .test('model-deck-quantity', function (items, context) {
            const commandersQuantity = Number(
              context.parent.composableDevice?.labels?.commandersQuantity
            );
            if (
              !isNaN(commandersQuantity) &&
              items.filter((item) => item && item?.trim()?.length > 0)
                ?.length !== Number(commandersQuantity)
            )
              return this.createError({
                message: polyglot.t(
                  'fours_fringand.oven.devices_quantity_must_equal'
                ),
              });

            return true;
          }),
      }),
    [polyglot]
  );

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        await dispatch(
          postGroup(
            values,
            logo ? logo : undefined,
            groupIdParent ? values.parentGroupID ?? '' : undefined
          )
        );
        showSuccess(polyglot.t('composable_device.save_successful_message'));
      } catch (error: any) {
        showError(polyglot.t('composable_device.failed_to_create'));
      } finally {
        closeAddComposableDevice();
      }
    },
  });

  return (
    <DialogModal
      open={showAddComposableDevice}
      onClose={closeAddComposableDevice}
      buttonActions={<></>}
      PaperProps={{ style: { maxWidth: '90vw' } }}
      sx={{
        '& .MuiDialogActions-root': { padding: '0' },
        '& .MuiBox-root': { margin: '0' },
        '& .MuiDialogContent-root': { padding: '0 2rem 4rem 2rem' },
      }}
    >
      <ModalContent icon={<></>}>
        <Typography variant="h2" textAlign={'start'}>
          {polyglot.t('composable_device.add_composable_device')}
        </Typography>
        <ComposableDeviceForm
          formik={formik}
          image={image}
          logo={logo}
          setImage={setImage}
          setLogo={setLogo}
          closeAddComposableDevice={closeAddComposableDevice}
        />
      </ModalContent>
    </DialogModal>
  );
}
